import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { FLEX_CONTAINER_COLUMN, H_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { anyReallyAny } from "@cervval.js/common/src/tools/object.tools";
import { CjsImagePicker, ImagePickerOptions } from "../../../pickers/imagePicker/imagePicker";
import { CatchErrorWithDialog } from "../../../tools/catchErrorWithDialog.decorator";
import { CjsEditor, EditorOptions, EditorSetValueOptions, defaultEditorOptions } from "../../editor";
import { FileWithLink } from "../../fileListEditor/fileListEditor";

/** Image file */
export type ImageWithLink = FileWithLink;

/** Options for ImageEditor */
export interface ImageEditorOptions extends EditorOptions {

    /** on new image added */
    onNewImageAdded?: (image: ImageWithLink) => Promise<ImageWithLink>;

    /** On image deleted */
    onImageDeleted?: (image: ImageWithLink) => Promise<void>;

}

/** Options for ImageEditor */
export const defaultImageEditorOptions: ImageEditorOptions = {
    ...defaultEditorOptions
};

@CustomElement({
    name: "cjs-image-editor",
    template: require("./imageEditor.html"),
    elementCssClasses: ["cjs-editor", FLEX_CONTAINER_COLUMN, H_100]
})
export class CjsImageEditor extends CjsEditor<ImageWithLink> {

    /** components */
    @TypedHtmlElements({ ensureLoadedClasses: [CjsImagePicker] })
    public readonly components!: {
        imagePicker: CjsImagePicker
    };

    @TypedWebComponentOptions(defaultImageEditorOptions)
    public readonly options!: WebComponentOptions<ImageEditorOptions>;

    /** Access to options of ImagePicker */
    public get imagePickerOptions(): WebComponentOptions<ImagePickerOptions> {
        return this.imagePicker.options;
    }

    /** Image picker of editor */
    public get imagePicker(): CjsImagePicker {
        return this.components.imagePicker;
    }

    /** Last image set inside editor */
    private _lastImageWithLinkSet?: ImageWithLink;

    constructor(options?: Partial<ImageEditorOptions>, imagePickerOptions?: Partial<ImagePickerOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
        this.imagePickerOptions.setWithoutRefresh(imagePickerOptions);
    }

    /**
     * Initialize components, input components and callbacks after the element is added to the dom and _components is ready
     */
    public override init(): void {
        super.init();
        this.imagePicker.valueChangeEvent.on(() => this._onInputValueChange());
    }

    /** Refresh editor */
    public override refresh(): void {
        super.refresh();
        this.imagePicker.refresh();
    }

    /**
     * Set editor value 
     * @param value 
     * @param options 
     * @returns true if value changed
     */
    public override setValue(value: ImageWithLink | undefined, options?: EditorSetValueOptions): boolean {

        if (this._value !== value) {
            this._value = value;

            // Set image in picker, or clear it if value is undefined
            this._refreshInputValue();

            if (options?.refreshIfChanged) {
                this.refresh();
            }
            if (options?.dispatchEvents) {
                this._dispatchValueChangedEvent();
            }

            // Saves the current image in another variable in case of deletion
            if (this._value != null) {
                this._lastImageWithLinkSet = this._value;
            }
            return true;
        }

        return false;
    }

    /**
     * Gets the value corresponding to the current input value
     */
    protected _getValueFromInputComponent(): ImageWithLink | undefined {
        return this._value;
    }

    /** Update image */
    private async _onNewImage(): Promise<void> {

        // Gets the images from the picker
        const newImage = this.imagePicker.getImage();
        if (newImage == null) { return; }// No image to upload

        // Casts the File into ImageWithLink
        const imageWithLink: ImageWithLink = { file: newImage };

        // Promise which looks if the image is added or not and update the value
        const onNewImageProm = this.options.onNewImageAdded?.(imageWithLink) ?? Promise.resolve(imageWithLink);
        await onNewImageProm.then(res => {
            this._value = res; // New value
        });
    }

    /** Delete image */
    private async _onDeleteImage(): Promise<void> {
        // Deletes only if the _lastImageWithLinkSet is set (an image is set)
        if (this._lastImageWithLinkSet != null) {
            // Promise which looks if the image is deleted or not and update the value
            const onImageDeleteProm = this.options.onImageDeleted?.(this._lastImageWithLinkSet) ?? Promise.resolve();
            await onImageDeleteProm.then(_ => {
                if (this.imagePicker.getImage() == null) {
                    this._value = undefined;// Clear value if no image in picker
                }
            });
        }
    }

    /** Get image picker as input for this editor */
    public getInput(): CjsImagePicker {
        return this.imagePicker;
    }

    /** Return name of image uploaded */
    public convertValueToInputValue(value: ImageWithLink | undefined): string {
        return value?.file.name ?? "";
    }

    /**
     * On filePicker update event is trigger
     * 
     */
    @CatchErrorWithDialog()
    protected override async _onInputValueChange(event?: anyReallyAny): Promise<void> {

        // Changes the value and if no image is defined (change has occurred), it will be deleted.
        await this._onNewImage();
        await this._onDeleteImage();

        // Dispatch onchange event
        this._dispatchValueChangedEvent();
    }

    /** Update disabled attribute on input component */
    protected _refreshDisabled(): void {
        this.imagePickerOptions.setAndRefreshIfChanged({ disabled: this.disabled });
    }

    /**
     * Update the input value from stored value
     */
    protected _refreshInputValue(): void {
        const image = this._value?.file;
        // If there is no image, cleans the picker, otherwise updates it.
        if (!image) {
            this.imagePicker.cleanDisplay({ doNotDispatchEvent: true });
        }
        else {
            this.imagePicker.setImage(image);
        }
    }

}
